import React from 'react';
import { connect } from 'react-redux';
import { Table } from 'semantic-ui-react';
import findResult from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import { columnName, columnSelect, columnVisibility } from '@/constants/fields';
import urls, { codeUrl } from '@/constants/urls';
import paging, { fitToPage, goToCodePage } from '@/hooks/paging';
import TableWrapper from '@/components/table/TableWrapper';
import DatasetsTableHeader from '@/datasets/components/DatasetsTableHeader';
import DatasetsTableRows from '@/datasets/components/DatasetsTableRows';
import NoRowsWithPagination from '@/components/table/NoRowsWithPagination';
import CohortDatasetsFilters from './CohortDatasetsFilters';
import { fetchCohortDatasets, toggleCohortDatasetSelected } from '../actions';

const columns = [
  columnSelect,
  columnName,
  columnVisibility,
];

const SelectCohortDatasets = ({
  canCreateDataset,
  category,
  code,
  currentPage,
  datasets,
  fetchData,
  onCheckBoxClick,
  newDatasetCode,
  pageCount,
  pageSize,
  readErrors,
  readStatus,
  theme,
  total,
}) => {
  paging.useFetchByCode(fetchData, category, code, readStatus);
  const { items, pageNumber } = fitToPage(datasets, currentPage, pageSize);
  const ref = React.useRef(null);

  return (
    <div className="ReactTable aridhia-select-cohort-datasets" ref={ref}>
      <TableWrapper sticky celled className="aridhia-datasets-table" columns={columns} selectable>
        <DatasetsTableHeader
          datasets={items}
          readStatus={readStatus}
          readErrors={readErrors}
          currentPage={currentPage}
          columns={columns}
          hideColumns
        >
          <CohortDatasetsFilters category={category} code={code} className="aridhia-table-filter" />
        </DatasetsTableHeader>
        <Table.Body>
          <DatasetsTableRows
            items={items}
            columns={columns}
            onCheckBoxClick={(value) => onCheckBoxClick(category, code, value)}
            selectable
            theme={theme}
          />
        </Table.Body>
      </TableWrapper>
      <NoRowsWithPagination
        canCreate={canCreateDataset}
        currentPage={pageNumber}
        entity="dataset"
        goToPage={({ page }) => goToCodePage(fetchData, category, code, page, ref)}
        icon="table"
        items={items}
        pageCount={pageCount}
        readErrors={readErrors}
        readStatus={readStatus}
        total={total}
        url={codeUrl(urls.DATASET, newDatasetCode, '#')}
      />
    </div>
  );
};

const accessControl = (role, exists) => (
  {
    exists,
    canCreateDataset: exists ? role.permissions.datasets.create : false,
  }
);

const withoutItem = (newDatasetCode, role) => (
  {
    ...accessControl(role, false),
    newDatasetCode,
    readStatus: states.PENDING,
    datasets: [],
  }
);

const withItem = (datasets, newDatasetCode, role, theme) => ({
  ...accessControl(role, true),
  currentPage: datasets.currentPage,
  datasets: datasets.results,
  newDatasetCode,
  pageCount: datasets.totalPages,
  pageSize: datasets.pageSize,
  readStatus: datasets.readStatus,
  readErrors: datasets.readErrors,
  theme,
  total: datasets.count,
});

const mapStateToProps = ({
  auth: { role },
  cohorts: { cohorts },
  datasets: { newDatasetCode },
  themes: { theme },
}, { category, code }) => {
  const { datasets } = findResult(cohorts, category, code) || {};
  return datasets ? withItem(datasets, newDatasetCode, role, theme) : withoutItem(newDatasetCode, role);
};

const mapDispatchToProps = {
  fetchData: fetchCohortDatasets,
  onCheckBoxClick: toggleCohortDatasetSelected,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectCohortDatasets);
